import React, { useState, useEffect } from "react";
import { Table, Input, Select, Button, Card, Row, Col, Typography, Modal } from "antd";
import { SearchOutlined, EyeOutlined, SyncOutlined } from "@ant-design/icons";
import Heading from "../../../components/Heading";
// import API from "../../../api";
// import urls from "../../../api/urls";
import "./style.scss";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const APILog = () => {
  const [apiLogs, setApiLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [selectedPayload, setSelectedPayload] = useState(null);
  const [payloadModalVisible, setPayloadModalVisible] = useState(false);
  const [payloadType, setPayloadType] = useState("request"); // "request" or "response"
  const [services, setServices] = useState([]);

  // Mock data for demonstration - replace with actual API calls
  const mockServices = [
    { value: "booking-service", label: "Booking Service" },
    { value: "manifest-service", label: "Manifest Service" },
    { value: "tracking-service", label: "Tracking Service" },
    { value: "lcr-service", label: "LCR Service" },
    { value: "eway-service", label: "Eway Service" }
  ];

  const mockApiLogs = [
    {
      id: 1,
      awbNumber: "25054200267073",
      bagNumber: "BAG001",
      icrNumber: "ICR123456",
      api: "/api/v1/shipment/track",
      requestTimestamp: "2024-01-15 10:30:25",
      responseTimestamp: "2024-01-15 10:30:26",
      requestPayload: {
        query: {
          awbNumber: "25054200267073"
        },
        params: {},
        headers: {
          "smcs-premise-id": "55",
          "user-id": "unknown"
        }
      },
      responsePayload: {
        status: "success",
        data: {
          awbNumber: "25054200267073",
          status: "In Transit",
          location: "Hub Mumbai"
        }
      }
    },
    {
      id: 2,
      awbNumber: "25054200267074",
      bagNumber: "BAG002",
      icrNumber: "ICR123457",
      api: "/api/v1/manifest/create",
      requestTimestamp: "2024-01-15 11:15:30",
      responseTimestamp: "2024-01-15 11:15:32",
      requestPayload: {
        manifestData: {
          manifestId: "MAN001",
          shipments: ["25054200267074", "25054200267075"]
        },
        headers: {
          "smcs-premise-id": "55",
          "user-id": "hub_operator"
        }
      },
      responsePayload: {
        status: "success",
        manifestId: "MAN001",
        message: "Manifest created successfully"
      }
    }
  ];

  useEffect(() => {
    fetchServices();
    fetchApiLogs();
  }, []);

  const fetchServices = async () => {
    try {
      // For now, use mock services. Replace with actual API call when backend is ready
      // const response = await API.get(urls.GET_API_LOG_SERVICES);
      // setServices(response.data);
      setServices(mockServices);
    } catch (error) {
      console.error("Error fetching services:", error);
      setServices(mockServices);
    }
  };

  const fetchApiLogs = async (searchParams = {}) => {
    setLoading(true);
    try {
      // For now, use mock data. Replace with actual API call when backend is ready
      // const response = await API.get(urls.GET_API_LOGS, { params: searchParams });
      // setApiLogs(response.data);
      
      // Simulate API call with mock data
      setTimeout(() => {
        let filteredLogs = mockApiLogs;
        
        if (searchParams.search) {
          filteredLogs = filteredLogs.filter(log => 
            log.awbNumber.toLowerCase().includes(searchParams.search.toLowerCase()) ||
            log.bagNumber.toLowerCase().includes(searchParams.search.toLowerCase()) ||
            log.icrNumber.toLowerCase().includes(searchParams.search.toLowerCase()) ||
            log.api.toLowerCase().includes(searchParams.search.toLowerCase())
          );
        }
        
        if (searchParams.service) {
          filteredLogs = filteredLogs.filter(log => 
            log.api.includes(searchParams.service)
          );
        }
        
        setApiLogs(filteredLogs);
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error("Error fetching API logs:", error);
      setApiLogs(mockApiLogs);
      setLoading(false);
    }
  };

  const handleSearch = () => {
    const searchParams = {
      search: searchText,
      service: selectedService
    };
    fetchApiLogs(searchParams);
  };

  const handleViewPayload = (record, type) => {
    setSelectedPayload(record);
    setPayloadType(type);
    setPayloadModalVisible(true);
  };

  const columns = [
    {
      title: "AWB Number",
      dataIndex: "awbNumber",
      key: "awbNumber",
      width: 150,
      sorter: (a, b) => a.awbNumber.localeCompare(b.awbNumber),
    },
    {
      title: "Bag Number",
      dataIndex: "bagNumber",
      key: "bagNumber",
      width: 120,
      sorter: (a, b) => a.bagNumber.localeCompare(b.bagNumber),
    },
    {
      title: "ICR Number",
      dataIndex: "icrNumber",
      key: "icrNumber",
      width: 120,
      sorter: (a, b) => a.icrNumber.localeCompare(b.icrNumber),
    },
    {
      title: "API",
      dataIndex: "api",
      key: "api",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Request Timestamp",
      dataIndex: "requestTimestamp",
      key: "requestTimestamp",
      width: 180,
      sorter: (a, b) => new Date(a.requestTimestamp) - new Date(b.requestTimestamp),
    },
    {
      title: "Response Timestamp",
      dataIndex: "responseTimestamp",
      key: "responseTimestamp",
      width: 180,
      sorter: (a, b) => new Date(a.responseTimestamp) - new Date(b.responseTimestamp),
    },
    {
      title: "Request Payload",
      key: "requestPayload",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewPayload(record, "request")}
        >
          View
        </Button>
      ),
    },
    {
      title: "Response Payload",
      key: "responsePayload",
      width: 120,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewPayload(record, "response")}
        >
          View
        </Button>
      ),
    },
  ];

  const tableLoading = {
    spinning: loading,
    indicator: <SyncOutlined spin />,
  };

  return (
    <div className="api-log-container">
      <Heading title="API Log" />
      
      {/* Search and Service Selection */}
      <Card className="search-card" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Input
              placeholder="Search by AWB, Bag Number, ICR Number, or API"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              onPressEnter={handleSearch}
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Select Service"
              value={selectedService}
              onChange={setSelectedService}
              style={{ width: "100%" }}
              allowClear
            >
              {services.map(service => (
                <Option key={service.value} value={service.value}>
                  {service.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Button type="primary" onClick={handleSearch} loading={loading}>
              Search
            </Button>
          </Col>
        </Row>
      </Card>

      {/* API Log Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={apiLogs}
          rowKey="id"
          loading={tableLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Payload Viewer Modal */}
      <Modal
        title={`${payloadType === "request" ? "Request" : "Response"} Payload`}
        open={payloadModalVisible}
        onCancel={() => setPayloadModalVisible(false)}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        {selectedPayload && (
          <div className="payload-viewer">
            <Title level={5}>
              AWB: {selectedPayload.awbNumber} | 
              API: {selectedPayload.api}
            </Title>
            <TextArea
              value={JSON.stringify(
                payloadType === "request" 
                  ? selectedPayload.requestPayload 
                  : selectedPayload.responsePayload,
                null,
                2
              )}
              rows={20}
              readOnly
              style={{ fontFamily: "monospace", fontSize: "12px" }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default APILog;
